/** Tiny DOM helpers — the CMS has no framework. */
export type Child = Node | string | null | undefined | false | Child[];

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === 'value' && 'value' in node) (node as HTMLInputElement).value = String(v);
    else if (k === 'checked' && 'checked' in node) (node as HTMLInputElement).checked = !!v;
    else if (k === 'selected' && 'selected' in node) (node as HTMLOptionElement).selected = !!v;
    else if (k === 'disabled' && 'disabled' in node) (node as HTMLButtonElement).disabled = !!v;
    else if (k === 'dataset' && typeof v === 'object') Object.assign(node.dataset, v);
    else node.setAttribute(k, String(v));
  }
  append(node, children);
  return node;
}

export function append(node: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(node, c);
    else node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
}

export function clear(node: HTMLElement): HTMLElement {
  node.replaceChildren();
  return node;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(message: string, kind: 'ok' | 'error' = 'ok'): void {
  let t = document.getElementById('toast');
  if (!t) { t = el('div', { id: 'toast' }); document.body.appendChild(t); }
  t.textContent = message;
  t.className = `show ${kind}`;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { t!.className = ''; }, kind === 'error' ? 6000 : 2500);
}

export function field(label: string, input: HTMLElement, hint?: string): HTMLElement {
  return el('label', { class: 'field' }, el('span', { class: 'label' }, label), input, hint ? el('small', {}, hint) : null);
}

export function select(options: Array<{ value: string; label: string }>, value: string, attrs: Record<string, unknown> = {}): HTMLSelectElement {
  return el('select', attrs, options.map((o) => el('option', { value: o.value, selected: o.value === value }, o.label)));
}

export function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number): (...a: A) => void {
  let t: ReturnType<typeof setTimeout> | null = null;
  return (...a: A) => { if (t) clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
}

export function confirmDialog(message: string): boolean {
  return window.confirm(message);
}

export const COLOURS = ['black', 'red', 'green', 'yellow', 'blue', 'magenta', 'cyan', 'white'] as const;
export const COLOUR_HEX = ['#000', '#f00', '#0f0', '#ff0', '#00f', '#f0f', '#0ff', '#fff'];
